/**
 * Shared timescale options for the AgenticV2 param cards.
 *
 * All three param cards (Chart Visualization, Driver Analysis, Edit
 * Scenarios) render the same three-way toggle, so the option list and the
 * "which are selectable" rule live here rather than three times over.
 *
 * `selectable_timescales` comes from the backend and lists the timescales
 * the locked Output is actually reported at on the in-scope model
 * instance. Absent or empty means unconstrained: every option stays
 * enabled, which is exactly how the control behaved before the field
 * existed. That is also what every card already in conversation history
 * looks like, so old cards keep rendering normally.
 */

export type TimescaleLiteral = 'monthly' | 'quarterly' | 'yearly';

export const TIMESCALE_OPTIONS: {
  value: TimescaleLiteral;
  label: string;
}[] = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

export const UNAVAILABLE_TIMESCALE_TOOLTIP =
  'Not reported for this output on this model instance';

/**
 * Parse the backend field defensively — it arrives as untyped card
 * content. Returns null (unconstrained) for anything unusable: absent,
 * not an array, empty, or containing no recognised timescale.
 */
export function parseSelectableTimescales(
  raw: unknown
): TimescaleLiteral[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const values = raw.filter(
    (value): value is TimescaleLiteral =>
      value === 'monthly' || value === 'quarterly' || value === 'yearly'
  );
  return values.length > 0 ? values : null;
}

export function isTimescaleSelectable(
  option: TimescaleLiteral,
  selectable: TimescaleLiteral[] | null
): boolean {
  return selectable === null || selectable.includes(option);
}
